import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import patientService from '../../services/patientService';
import practiceService from '../../services/practiceService';

// Async thunks
export const createPatient = createAsyncThunk(
  'patient/createPatient',
  async ({ practiceId, patientData }, { rejectWithValue }) => {
    try {
      const response = await patientService.createPatient(practiceId, patientData);
      practiceService.invalidatePracticePatientsCache(practiceId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create patient');
    }
  }
);

export const fetchPatients = createAsyncThunk(
  'patient/fetchPatients',
  async ({ practiceId, params = {} }, { rejectWithValue }) => {
    try {
      const response = await patientService.getPatients(practiceId, params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch patients');
    }
  }
);

export const fetchPatient = createAsyncThunk(
  'patient/fetchPatient',
  async ({ practiceId, patientId }, { rejectWithValue }) => {
    try {
      const response = await patientService.getPatient(practiceId, patientId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch patient');
    }
  }
);

export const updatePatient = createAsyncThunk(
  'patient/updatePatient',
  async ({ practiceId, patientId, patientData }, { rejectWithValue }) => {
    try {
      const response = await patientService.updatePatient(practiceId, patientId, patientData);
      practiceService.invalidatePracticePatientsCache(practiceId);
      return response.data;
    } catch (error) {
      const d = error.response?.data;
      const msg =
        (d?.message && d?.error ? `${d.message}: ${d.error}` : null) ||
        d?.error ||
        d?.message ||
        'Failed to update patient';
      return rejectWithValue(msg);
    }
  }
);

export const deletePatient = createAsyncThunk(
  'patient/deletePatient',
  async ({ practiceId, patientId }, { rejectWithValue }) => {
    try {
      const response = await patientService.deletePatient(practiceId, patientId);
      practiceService.invalidatePracticePatientsCache(practiceId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete patient');
    }
  }
);

const initialState = {
  patients: [],
  currentPatient: null,
  isLoading: false,
  error: null,
};

const patientSlice = createSlice({
  name: 'patient',
  initialState,
  reducers: {
    setPatients: (state, action) => {
      state.patients = action.payload;
    },
    setCurrentPatient: (state, action) => {
      state.currentPatient = action.payload;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create patient
      .addCase(createPatient.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createPatient.fulfilled, (state, action) => {
        state.isLoading = false;
        state.patients.push(action.payload.data);
      })
      .addCase(createPatient.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch patients
      .addCase(fetchPatients.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPatients.fulfilled, (state, action) => {
        state.isLoading = false;
        state.patients = action.payload.data || [];
      })
      .addCase(fetchPatients.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch single patient
      .addCase(fetchPatient.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPatient.fulfilled, (state, action) => {
        state.isLoading = false;
        const patientData = action.payload?.data || action.payload;
        state.currentPatient = patientData;
      })
      .addCase(fetchPatient.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update patient
      .addCase(updatePatient.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePatient.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedPatient = action.payload?.data || action.payload;
        if (updatedPatient?.id) {
          const idStr = String(updatedPatient.id);
          const index = state.patients.findIndex((p) => String(p.id) === idStr);
          const mergedListRow =
            index !== -1 ? { ...state.patients[index], ...updatedPatient } : updatedPatient;
          if (index !== -1) {
            state.patients[index] = mergedListRow;
          } else {
            state.patients.push(mergedListRow);
          }
          const prev = state.currentPatient;
          state.currentPatient =
            prev && String(prev.id) === idStr ? { ...prev, ...updatedPatient } : updatedPatient;
        }
      })
      .addCase(updatePatient.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete patient
      .addCase(deletePatient.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deletePatient.fulfilled, (state, action) => {
        state.isLoading = false;
        state.patients = state.patients.filter(p => p.id !== action.meta.arg.patientId);
      })
      .addCase(deletePatient.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setPatients,
  setCurrentPatient,
  setLoading,
  setError,
  clearError,
} = patientSlice.actions;

export default patientSlice.reducer;
